/**
 * importLanding — plain-English "here is exactly what this row will do to your
 * catalog", for every import approval screen.
 *
 * An import review that only says "new" or "price change" leaves the most
 * consequential outcome invisible: whether a line becomes a SECOND ITEM you
 * now have to count twice, or a second WAY TO BUY an item you already count.
 * describeLanding spells that out per row so approval is informed and
 * disapproval is one click.
 *
 * Five landings:
 *   skip         nothing happens
 *   reject       explicitly disapproved — nothing happens, and it is not
 *                quietly filed under "unchanged"
 *   update_price the matched vendor product's price moves
 *   new_option   a new vendor product joins an item you already count
 *   new_item     a brand-new countable item, with this line as its first
 *                vendor product
 *
 * Pure and DOM-free so both the React approval table and the static import
 * review can share one description.
 */

export const LANDING = Object.freeze({
  SKIP: 'skip',
  REJECT: 'reject',
  UPDATE_PRICE: 'update_price',
  NEW_OPTION: 'new_option',
  NEW_ITEM: 'new_item',
});

/** Classification values the approval table stores on a row. */
export const CLASSIFICATIONS = Object.freeze({
  UNCHANGED: 'unchanged',
  PRICE_CHANGE: 'price_change',
  NEW_OPTION: 'new_option',
  NEW: 'new',
  REJECTED: 'rejected',
});

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const money = (value) => {
  const parsed = toFiniteNumber(value);
  return parsed === null ? null : `$${parsed.toFixed(2)}`;
};

const nameOf = (item) => {
  const raw = (item?.description ?? '').toString().trim();
  return raw || 'this line';
};

const vendorOf = (item) => {
  const raw = (item?.vendor ?? '').toString().trim();
  return raw || null;
};

/**
 * What one purchase package IS, from facts the row states — the vendor's own
 * pack wording first, then an explicit multi-unit case, then the bare unit.
 */
const packageOf = (item) => {
  const pack = (item?.pack_size ?? '').toString().trim();
  if (pack) return pack;
  const unit = (item?.unit ?? '').toString().trim();
  const qty = toFiniteNumber(item?.case_quantity);
  if (unit && qty !== null && qty > 1) return `${qty} × ${unit}`;
  return unit || null;
};

/**
 * "$19.95 buys one 1 L bottle." — the sentence that stops a package price
 * being read as a case price.
 *
 * A bar's order guide prices most lines by the bottle, and the row stores that
 * in a field named case_price with case_quantity 1. Nothing was wrong with the
 * arithmetic; what was missing was any sentence saying WHICH package the
 * figure buys, so "$19.95" next to a spirit read as a case of twelve. Silent
 * when the row states no price — there is nothing to be clear about.
 */
const packagePriceClause = (item) => {
  const price = money(item?.case_price);
  if (!price) return '';
  const pack = packageOf(item);
  return ` ${price} buys one ${pack || 'purchase unit'}.`;
};

/**
 * Maps a row's classification to a landing, with the sentence that describes
 * it and any warnings worth raising before approval.
 *
 * @param {Object}  item                      approval row
 * @param {string}  item._classification      deterministic verdict
 * @param {string} [item._classificationOverride] the user's override
 * @param {Object} [item._existingItem]       the matched catalog item
 * @param {Object} [item._possibleMatch]      a SUSPECTED existing item ({id,
 *                                            description, vendor, score,
 *                                            from_this_import}) — the import
 *                                            proposed a link and is waiting on
 *                                            an answer. Never the same thing
 *                                            as _existingItem.
 * @param {boolean} [item._existingItemFromThisImport] the matched item was
 *                                            created by THIS import session
 *                                            rather than found in the catalog
 * @param {Object} [context]
 * @param {number} [context.existingVendorCount] vendors already on the match
 * @param {number} [context.existingOptionCount] vendor products on the match
 * @param {string} [context.countUnit]        the matched item's count unit
 * @returns {{action: string, title: string, detail: string|null, warnings: string[]}}
 */
export function describeLanding(item, context = {}) {
  const classification = item?._classificationOverride || item?._classification || null;
  const existing = item?._existingItem || null;
  const vendor = vendorOf(item);
  const name = nameOf(item);
  const existingName = (existing?.description ?? '').toString().trim() || 'the matched item';
  const unit = context.countUnit || existing?.unit || item?.unit || null;
  const warnings = [];

  if (item?._vendorResolution?.action === 'needs_review') {
    const proposed = (item._vendorResolution.proposed_vendor_name ?? '').toString().trim();
    warnings.push(
      `Vendor "${vendor || 'Unknown'}" is ambiguous and was not linked or saved as a vendor.`
      + `${proposed ? ` Review whether it means ${proposed}.` : ' Choose the correct vendor or create one only if this is a distinct business.'}`,
    );
  }

  if (classification === CLASSIFICATIONS.REJECTED) {
    return {
      action: LANDING.REJECT,
      title: 'Not imported',
      detail: `"${name}" is disapproved and will be left out of your catalog entirely.`,
      warnings,
    };
  }

  if (classification === CLASSIFICATIONS.UNCHANGED) {
    return {
      action: LANDING.SKIP,
      title: 'No change',
      detail: existing
        ? `"${existingName}" already has this price. Nothing is written.`
        : 'Nothing is written for this line.',
      warnings,
    };
  }

  if (classification === CLASSIFICATIONS.PRICE_CHANGE) {
    if (!existing) {
      warnings.push('No catalog item is matched, so there is no price to update.');
      return {
        action: LANDING.SKIP,
        title: 'Nothing to update',
        detail: 'This line is marked as a price change but is not matched to a catalog item.',
        warnings,
      };
    }
    const from = money(existing.case_price);
    const to = money(item?.case_price);
    const priced = from && to ? ` ${from} → ${to} per package.` : '';
    return {
      action: LANDING.UPDATE_PRICE,
      title: 'Price update',
      detail: `Updates ${vendor ? `the ${vendor} product on ` : ''}"${existingName}".${priced}`
        + ' Counting, PARs, and recipes are untouched.',
      warnings,
    };
  }

  if (classification === CLASSIFICATIONS.NEW_OPTION) {
    if (!existing) {
      warnings.push('No catalog item is matched, so there is nothing to attach this vendor product to.');
      return {
        action: LANDING.NEW_ITEM,
        title: 'New item',
        detail: `No match found, so "${name}" becomes a new item.`,
        warnings,
      };
    }
    const before = toFiniteNumber(context.existingVendorCount);
    const after = before === null ? null : before + 1;
    const vendorLine = after === null
      ? ''
      : ` "${existingName}" will have ${after} vendor${after === 1 ? '' : 's'}.`;
    if (toFiniteNumber(item?.case_price) === null) {
      warnings.push('No price on this line, so the new vendor product has no comparable cost yet.');
    }
    // When the ROW is the pre-existing item itself (an import line landed on
    // it as a vendor product), item.vendor is the existing item's vendor —
    // the sentence must name the IMPORTING vendor, carried as _importVendor.
    const importVendor = ((item?._importVendor ?? '').toString().trim()) || vendor;
    // "An item you already count" is only true when the item predates the
    // import. On a first import it is a line from the same batch, and saying
    // otherwise invents a history the store does not have.
    const sameSession = item?._existingItemFromThisImport === true;
    return {
      action: LANDING.NEW_OPTION,
      title: sameSession
        ? 'Another way to buy an item from this import'
        : 'New way to buy an item you already count',
      detail: `Adds ${importVendor ? `a ${importVendor} product` : 'a vendor product'} to "${existingName}"`
        + `${sameSession ? ', which this import just created' : ''}.${vendorLine}`
        + ` You still count it once, in ${unit || 'its count unit'}.`,
      warnings,
    };
  }

  // Default: a brand-new countable item.
  if (existing) {
    warnings.push(
      `"${existingName}"${item?._existingItemFromThisImport === true ? ', also in this import,' : ''}`
      + ' already looks like this product. Importing as new creates a second'
      + ' item you will have to count separately.',
    );
  }
  // The import matched this line to something and is not sure enough to act.
  // That doubt has to reach the person approving, or "new item" reads as a
  // finding when it is really an unanswered question — the row lands, and the
  // duplicate only surfaces weeks later as a related-products suggestion.
  const possible = item?._possibleMatch || null;
  if (possible && !existing) {
    const possibleName = (possible.description ?? '').toString().trim();
    const possibleVendor = (possible.vendor ?? '').toString().trim();
    // Where the twin came from changes what the reader has to do about it.
    // A catalog twin is a duplicate of something they already count; a twin
    // from this same import is two lines of one paste describing one product,
    // and telling them to check a catalog that is empty is worse than saying
    // nothing. This is the sentence a first import into a brand-new store
    // showed on nineteen rows at once.
    const sameSession = possible.from_this_import === true;
    // Named or not, the sentence must read once. Falling back to a subject
    // that already said "in your catalog" and then appending the clause again
    // produced "This may already be an item already in your catalog, which is
    // already in your catalog."
    const named = possibleName
      ? `"${possibleName}"${possibleVendor ? ` (${possibleVendor})` : ''}`
      : null;
    if (sameSession) {
      warnings.push(
        `${named ? `${named} in this import` : 'Another line in this import'} looks like the same`
        + ' product. Importing both creates two items you will have to count separately — mark'
        + ' this one "Another way to buy it" if they are the same product.',
      );
    } else {
      // Not an accusation. Two vendors selling one product is normal and
      // useful — the point is to offer the link, not to warn about a mistake.
      warnings.push(
        `This may be a similar item to ${named || 'an item in your catalog'}, forming another way`
        + ' to purchase it. Mark it "Another way to buy it" to count them as one product, or'
        + ' import as new to keep them separate.',
      );
    }
  }
  return {
    action: LANDING.NEW_ITEM,
    title: 'New item',
    detail: `Creates "${name}"${unit ? `, counted in ${unit}` : ''}`
      + `${vendor ? `, with ${vendor} as its first vendor product` : ''}.`
      + packagePriceClause(item),
    warnings,
  };
}

/**
 * Whether a review row must NOT be written to the catalog.
 *
 * Two different decisions produce the same outcome: "leave it as unchanged"
 * and "don't import this". Hosts partition the table into approved/rejected
 * and hand the rejected list to whatever undoes work the import already did,
 * so both must land on the same side — otherwise the disapprove control is a
 * lie in that channel.
 */
export function isDeclinedImportRow(item) {
  const classification = item?._classificationOverride || item?._classification || 'new';
  return classification === CLASSIFICATIONS.UNCHANGED
    || classification === CLASSIFICATIONS.REJECTED;
}

/**
 * Roll-up of what an approved batch will do, for the sentence above the
 * approve button. Rejected and skipped rows are reported separately so
 * "nothing happens to these" is visible rather than implied.
 */
export function summarizeLandings(items, contextFor = () => ({})) {
  const counts = {
    [LANDING.SKIP]: 0,
    [LANDING.REJECT]: 0,
    [LANDING.UPDATE_PRICE]: 0,
    [LANDING.NEW_OPTION]: 0,
    [LANDING.NEW_ITEM]: 0,
  };
  // What the import found out about RELATIONSHIPS, tallied apart from the
  // landing. A landing describes what one row does to the catalog on its own;
  // it cannot describe a proposal, because a proposal writes nothing until
  // it is accepted. So a batch that surfaced twelve related-product links in
  // the step before this one reported none of them here, and every proposed
  // row was silently filed under "new items" — the screens disagreed because
  // they were answering different questions without saying so.
  //   combined  the import joined this row to an existing item
  //   suggested the import proposed a join and is waiting on an answer
  // Rows carry it as `_importRelationship`, set from the same import outcomes
  // the related-products review renders.
  //
  // Each is split again by WHO the row was joined to, because "we matched this
  // to something you already stock" and "two lines of your own paste are the
  // same product" are different findings and only the first is about the
  // catalog. A first import into an empty store produces only the second, and
  // reporting it as the first is how the review claimed a brand-new store
  // already held nineteen of the products being imported into it.
  //   toExisting   the partner predates this import session
  //   withinImport the partner is another line from this same import
  const relationships = { combined: 0, suggested: 0, toExisting: 0, withinImport: 0 };
  let warnings = 0;
  (items || []).forEach((item) => {
    const landing = describeLanding(item, contextFor(item) || {});
    counts[landing.action] += 1;
    warnings += landing.warnings.length;
    const relationship = item?._importRelationship;
    if (relationship === 'combined' || relationship === 'suggested') {
      relationships[relationship] += 1;
      const partnerFromThisImport = relationship === 'combined'
        ? item?._existingItemFromThisImport === true
        : item?._possibleMatch?.from_this_import === true;
      if (partnerFromThisImport) relationships.withinImport += 1;
      else relationships.toExisting += 1;
    }
  });
  return {
    counts,
    relationships,
    warnings,
    actionable: counts[LANDING.UPDATE_PRICE] + counts[LANDING.NEW_OPTION] + counts[LANDING.NEW_ITEM],
  };
}

/**
 * One-line summary sentence, e.g.
 * "3 new items · 2 new ways to buy existing items · 1 price update · 4 skipped · 1 disapproved".
 *
 * Relationship figures come last and are deliberately phrased as links rather
 * than as items: a suggested link has not changed the catalog, so it must not
 * read like one of the landings above it. Reporting it at all is the point —
 * without it, an import that proposed a dozen related products announced only
 * the handful it had already written, and the number contradicted the review
 * step the user had just clicked through.
 */
export function summaryLine(summary) {
  if (!summary) return '';
  const { counts, relationships = {} } = summary;
  const parts = [];
  const push = (n, one, many) => { if (n > 0) parts.push(`${n} ${n === 1 ? one : many}`); };
  push(counts[LANDING.NEW_ITEM], 'new item', 'new items');
  push(counts[LANDING.NEW_OPTION], 'new way to buy an existing item', 'new ways to buy existing items');
  push(counts[LANDING.UPDATE_PRICE], 'price update', 'price updates');
  push(counts[LANDING.SKIP], 'skipped', 'skipped');
  push(counts[LANDING.REJECT], 'disapproved', 'disapproved');
  push(relationships.suggested, 'suggested related product to review', 'suggested related products to review');
  return parts.join(' · ') || 'Nothing to import';
}
